import { Controller } from "stimulus";

const CLICK_SELECTOR = "a[data-confirm], button[data-confirm], input[data-confirm]";

// Replaces the browser's native confirm() everywhere in the product UI, without touching call sites.
//
// Two paths, one dialog:
// - `data-confirm` (rails-ujs, which runs on both the dashboard and subscriber_v2): a capture-phase
//   click/submit listener swallows the event before rails-ujs or Turbo see it, asks, and on
//   "confirm" re-fires the original click/submit with `data-confirm` stripped for that one dispatch.
//   rails-ujs and Turbo then handle it exactly as they would have without a confirm, and rails-ujs
//   finds nothing to confirm, so no native dialog appears.
// - `data-turbo-confirm`: Turbo awaits the promise from its configured confirm method.
//
// Per-site copy overrides on the triggering element: `data-confirm-title`, `data-confirm-label`.
// The confirm label defaults to "Confirm", or "Delete" for destructive (DELETE) actions.
export default class extends Controller {
  static targets = ["dialog"];

  connect() {
    this.resolver = null;

    this.onClick = this.onClick.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.onBackdropClick = this.onBackdropClick.bind(this);
    this.onBeforeCache = this.onBeforeCache.bind(this);

    document.addEventListener("click", this.onClick, true);
    document.addEventListener("submit", this.onSubmit, true);
    document.addEventListener("turbo:before-cache", this.onBeforeCache);
    this.dialogTarget.addEventListener("click", this.onBackdropClick);

    this.installTurboConfirm();
  }

  disconnect() {
    document.removeEventListener("click", this.onClick, true);
    document.removeEventListener("submit", this.onSubmit, true);
    document.removeEventListener("turbo:before-cache", this.onBeforeCache);
    this.dialogTarget.removeEventListener("click", this.onBackdropClick);
    this.settle(false);
  }

  installTurboConfirm() {
    const turbo = window.Turbo;
    if (!turbo) {
      // Turbo may not have booted yet; retry once when the page has loaded.
      if (!this.turboRetried) {
        this.turboRetried = true;
        const retry = () => this.installTurboConfirm();
        document.addEventListener("turbo:load", retry, { once: true });
        document.addEventListener("DOMContentLoaded", retry, { once: true });
      }
      return;
    }

    const confirmMethod = (message, form, submitter) =>
      this.ask({
        message,
        title: this.attr("data-confirm-title", submitter, form),
        label: this.attr("data-confirm-label", submitter, form),
        destructive: this.isDestructive(submitter, form),
      });

    if (turbo.config && turbo.config.forms) {
      turbo.config.forms.confirm = confirmMethod;
    } else if (typeof turbo.setConfirmMethod === "function") {
      turbo.setConfirmMethod(confirmMethod);
    }
  }

  onClick(event) {
    if (event.defaultPrevented) return;
    const element = event.target.closest && event.target.closest(CLICK_SELECTOR);
    if (!element || element.disabled || !element.dataset.confirm) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    this.askFor(element, () => this.withoutConfirm([element], () => element.click()));
  }

  onSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.dataset.confirm) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const submitter = event.submitter;
    this.askFor(form, () =>
      this.withoutConfirm([form, submitter], () => form.requestSubmit(submitter || undefined)),
      submitter
    );
  }

  askFor(element, proceed, submitter = null) {
    this.ask({
      message: element.dataset.confirm,
      title: this.attr("data-confirm-title", submitter, element),
      label: this.attr("data-confirm-label", submitter, element),
      destructive: this.isDestructive(submitter, element, element.form || element.closest("form")),
    }).then((confirmed) => {
      if (confirmed) proceed();
    });
  }

  // Strips data-confirm for the duration of one synchronous re-dispatch; rails-ujs and Turbo both
  // read it synchronously while handling the event.
  withoutConfirm(elements, dispatch) {
    const stripped = elements
      .filter((el) => el && el.hasAttribute("data-confirm"))
      .map((el) => [el, el.getAttribute("data-confirm")]);
    stripped.forEach(([el]) => el.removeAttribute("data-confirm"));
    try {
      dispatch();
    } finally {
      stripped.forEach(([el, value]) => {
        if (el.isConnected) el.setAttribute("data-confirm", value);
      });
    }
  }

  ask({ message, title, label, destructive }) {
    // One dialog at a time; a second request while open is declined rather than queued.
    if (this.resolver) return Promise.resolve(false);

    // No default title: the heading only appears when the call site sets `data-confirm-title`.
    const titlePart = this.part("title");
    titlePart.textContent = title || "";
    titlePart.hidden = !title;
    const descriptionPart = this.part("description");
    descriptionPart.textContent = message;
    descriptionPart.classList.toggle("mt-2", Boolean(title));
    this.part("confirm").textContent = label || (destructive ? "Delete" : "Confirm");

    return new Promise((resolve) => {
      this.resolver = resolve;
      this.dialogController.open();
    });
  }

  onConfirm() {
    this.settle(true);
  }

  onCancel() {
    this.settle(false);
  }

  onBackdropClick(event) {
    if (event.target === this.dialogTarget) this.settle(false);
  }

  onBeforeCache() {
    this.settle(false);
  }

  settle(answer) {
    const resolve = this.resolver;
    this.resolver = null;
    if (this.dialogTarget.isConnected) this.dialogController.close();
    if (resolve) resolve(answer);
  }

  // First of the given elements carrying the attribute (submitter wins over its form).
  attr(name, ...elements) {
    const holder = elements.find((el) => el && el.hasAttribute(name));
    return holder ? holder.getAttribute(name) : null;
  }

  isDestructive(...elements) {
    return elements.some((el) => {
      if (!el) return false;
      const method = (el.dataset && (el.dataset.method || el.dataset.turboMethod)) || "";
      if (method.toLowerCase() === "delete") return true;
      const override = el.querySelector && el.querySelector("input[name='_method']");
      return !!override && override.value.toLowerCase() === "delete";
    });
  }

  part(name) {
    return this.dialogTarget.querySelector(`[data-confirm-dialog-part="${name}"]`);
  }

  get dialogController() {
    return this.application.getControllerForElementAndIdentifier(this.dialogTarget, "block--confirm-dialog-component");
  }
}
